import { ICompileService, IComponentController, IRootScopeService, IScope, ITimeoutService, material } from 'angular';
import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/gridstack-extra.min.css';
import { GridStack } from 'gridstack';
import template from './dashboard-page.component.html';
import type { AutoRefreshInterval } from '../dashboard-personalization.service';
import type { Dashboard, DashboardService, DashboardWidgetDTO, WidgetCategory, WidgetInstance, WidgetInstanceScope } from '../dashboard.service';
import { DashboardFiltersService } from '../dashboard-filters.service';
import { GridItemHTMLElement } from 'gridstack/dist/types';
import { ActionsContext, ActionUiZone } from '../../Shell/Pandora/model/action';
import type { MxContextService } from '../../Shell/Pandora/services/mx-context-service';
import type { EventService } from '../../App/services/event-service';
import { EventCodes } from '../../App/model/event-codes';
import { MxGridStackEngine } from './MxGridStackEngine';
import type { RoutingService } from '../../App/services/mx-routing-service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { I18n } from '../../i18n/mx-internationalization';
import type { SchemaService } from '../../Shell/PandoraSolutionBuilderAgent/services/schema-service';
import type { MxApplicationService } from '../../App/services/mx-application-service';
import emptyPageAssistentImg from '../../Shell/assets/images/modern_man-assistent-310.png';
import requiredFiltersImg from '../../Shell/assets/images/modern_girl.png';
import { DisplayMode } from '../../Shell/Pandora/model/display-modes';
import type { DataModelCacheService } from '../../Shell/PandoraSolutionBuilderAgent/services/data-model-cache.service';
import type { UserProfileService } from '../../App/services/user-profile-service';
import type { UserProfile } from '../../App/model/user-profile';
import type { PersonalizationService } from '../../App/services/personalization-service';
import type { MxFeatureTrackingService } from '../../App/services/mx-feature-tracking-service';
import { FeatureTrackingType, FeatureTrackingUseCases } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';

GridStack.registerEngine(MxGridStackEngine);

export const enum DashboardConfigurationMode {
	Disabled = 'disabled',
	ExistWidgets = 'existWidgets',
	ChooseCategory = 'chooseCategory',
	WidgetConfiguration = 'widgetConfiguration',
}

export class DashboardPageController implements IComponentController {
	public gridStack!: GridStack;
	public gridStackEngine!: MxGridStackEngine;
	public dashboard!: Dashboard;
	public dashboardLoader!: Promise<Dashboard>;
	public widgets!: DashboardWidgetDTO[];
	public configurationMode: DashboardConfigurationMode = DashboardConfigurationMode.Disabled;
	public embeddedMode = false;
	public reportMode = false;
	public onWidgetInstanceClick!: (params: { widgetInstance: WidgetInstance }) => void;
	public configurableWidget?: WidgetInstance;
	public highlightedWidget?: WidgetInstance;
	public emptyPageBody = '';
	public emptyPageAssistentImg: string = emptyPageAssistentImg;
	public requiredFiltersImg: string = requiredFiltersImg;
	private readonly debouncedChangeHandler: () => void;
	public errorMessage?: string;
	public errorTitle?: string;
	private gridBreakboints = [{ w: 960, c: 1 }];
	private componentId = '';
	public onInitialized: (params: { dashboard: Dashboard }) => void = () => {};
	public displayMode: DisplayMode = DisplayMode.Normal;
	private autoRefresherTimer?: NodeJS.Timeout;
	private mouseMovedTimeout?: NodeJS.Timeout;
	private mouseMoved = false;
	private mouseMovedFinishTime?: number;
	private profile!: UserProfile;
	private isDescrExpanded = true;
	public totalNeighborDashboards?: number;
	public currentDashboardIndex?: number;
	private preEditConfigurationMode: DashboardConfigurationMode = DashboardConfigurationMode.Disabled;

	static readonly $inject = [
		'$scope',
		'$timeout',
		'$compile',
		'$rootScope',
		'$mdUtil',
		'mx.sb.Dashboard.Service',
		'mx.sb.Dashboard.DashboardFiltersService',
		'mx.shell.AppsService',
		'mx.shell.ContextService',
		'mx.shell.EventService',
		'mx.shell.RoutingService',
		'mx.shell.NotificationService',
		'mx.internationalization',
		'mx.SolutionBuilderAgent.SchemaService',
		'mx.SolutionBuilderAgent.DataModelCacheService',
		'mx.shell.UserProfileService',
		'mx.SolutionBuilderAgent.PersonalizationService',
		'mx.shell.FeatureTrackingService',
	];

	constructor(
		private readonly $scope: IScope,
		private readonly $timeout: ITimeoutService,
		private readonly $compile: ICompileService,
		private readonly $rootScope: IRootScopeService,
		private readonly $mdUtil: material.IUtilService,
		private readonly dashboardService: DashboardService,
		private readonly dashboardFiltersService: DashboardFiltersService,
		private readonly applicationService: MxApplicationService,
		private readonly contextService: ReturnType<typeof MxContextService>,
		private readonly eventService: EventService,
		private readonly routingService: RoutingService,
		private readonly notificationService: NotificationService,
		private readonly i18n: I18n,
		private readonly schemaService: SchemaService,
		private readonly dataModelCacheService: DataModelCacheService,
		private readonly userProfileService: UserProfileService,
		private readonly personalizationService: ReturnType<typeof PersonalizationService>,
		private readonly featureTrackingService: MxFeatureTrackingService
	) {
		this.debouncedChangeHandler = this.$mdUtil.debounce(() => {
			this.handleGridChanges();
		}, 50) as () => void;
	}

	$onInit() {
		if (!this.embeddedMode) {
			// @ts-expect-error MxContextService doesn't have typings
			this.contextService.actionsContext = new ActionsContext([], [], ActionUiZone.SearchPage);
		}

		this.eventService.dispatchEvent(EventCodes.DashboardPageConfigurationModeChanged, {
			configurationMode: this.configurationMode,
			isInitializing: true,
		});

		setTimeout(() => {
			this.gridStack = GridStack.init(
				{
					staticGrid: true,
					cellHeight: '180px',
					margin: 0,
					float: true,
					resizable: {
						autoHide: true,
						handles: 'e, w, s, n',
					},
					columnOpts: {
						breakpoints: this.gridBreakboints,
						layout: 'list',
					},
				},
				`.${this.componentId || 'grid-stack'}`
			);

			this.gridStackEngine = this.gridStack.engine as MxGridStackEngine;

			this.gridStack.on('resizestart', (event: Event, el: GridItemHTMLElement) => {
				if (el.gridstackNode) {
					const node = el.gridstackNode;

					this.gridStackEngine.enableRowResizingMode(node);

					if ((node.x ?? 0) + (node.w ?? 0) === this.gridStack.getColumn()) {
						setTimeout(() => {
							// issue in gridstack. When resizing the widget with left border, maxWidth constrain for such resizing is calculated to the right side instead of to the left one
							GridStack.getDD().resizable(
								el,
								'option',
								'maxWidth',
								this.gridStack.cellWidth() * Math.min(node.maxW || Number.MAX_SAFE_INTEGER, (node.x ?? 0) + (node.w ?? 0))
							);
						});
					}
				}
			});

			this.gridStack.on('resize', (event: Event, el: GridItemHTMLElement) => {
				const resizingBorder = this.gridStackEngine.resizingBorder;
				if (resizingBorder) {
					const colLeft = resizingBorder === 'left' ? (el.gridstackNode!.x ?? 0) + (el.gridstackNode!.w ?? 0) : this.gridStack.getColumn() - (el.gridstackNode!.x ?? 0);
					// issue in gridstack. When resizing the widget with left border, maxWidth constrain for such resizing is calculated to the right side instead of to the left one
					GridStack.getDD().resizable(el, 'option', 'maxWidth', this.gridStack.cellWidth() * Math.min(el.gridstackNode!.maxW || Number.MAX_SAFE_INTEGER, colLeft));
				}
			});

			this.gridStack.on('resizestop', () => {
				this.gridStackEngine.disableRowResizingMode();
				this.debouncedChangeHandler();
				this.saveDashboard();
			});

			this.gridStack.on('dragstart', (event: Event, el: GridItemHTMLElement) => {
				const node = el.gridstackNode;
				if (node) {
					this.gridStackEngine.enableDragIntoRowMode(node);
				}
			});

			this.gridStack.on('dragstop', () => {
				this.gridStackEngine.disableDragIntoRowMode();
				this.gridStack.compact('compact');
				this.debouncedChangeHandler();
				this.saveDashboard();
			});

			this.gridStack.on('change', () => {
				this.debouncedChangeHandler();
			});

			this.dashboardLoader
				.then((dashboard) => {
					this.init(dashboard);
				})
				.catch((error) => {
					this.errorMessage = error && (error.message || error);
					this.errorTitle = error && error.title;
				});

			$('.grid-stack').attr('aria-label', this.i18n.get('shell.dashboards.ARIA-dashboardWidgets'));
		});

		this.prepareEmptyPage();
	}

	$onDestroy() {
		this.setDisplayMode(DisplayMode.Normal);
		if (this.mouseMovedTimeout) {
			clearInterval(this.mouseMovedTimeout);
		}
	}

	async init(dashboard: Dashboard) {
		this.dashboard = dashboard;

		this.isDescrExpanded = (await this.personalizationService.getDashboardDescrVisibility(this.dashboard.navigationId)) ?? true;

		if (this.dashboard.isDescrPersonalized) {
			this.profile = await this.userProfileService.get();
		}

		const templatesPromise = Promise.all(
			this.dashboard.widgetInstances
				.filter((widgetInstance) => !widgetInstance.hidden)
				.map((widgetInstance) => this.dashboardService.refreshWidgetInstance(widgetInstance, true, this.reportMode))
		);

		this.addWidgetsToGridster(this.dashboard.widgetInstances.filter((widgetInstance) => !widgetInstance.hidden));

		templatesPromise.then(() => {
			this.initDependentFilters();
			this.initInteractiveFilters();

			this.updateSizesAndPriorities();
			this.onInitialized({ dashboard: this.dashboard });

			this.eventService.dispatchEvent(EventCodes.DashboardPageInitialized, this);
		});

		if (this.embeddedMode && this.dashboard?.widgetInstances?.[0]?.isEditable) {
			this.editWidget(this.dashboard.widgetInstances[0]);
		}
	}

	private async initDependentFilters() {
		this.dashboard.filters.dependent?.onChange(this.$scope, async (dependentFilter) => {
			this.dashboardFiltersService.applyDependentFilters(this.dashboard, dependentFilter);
			this.updateRouteState();
		});
	}

	private updateRouteState() {
		this.dashboardService.navigateToDashboard(this.dashboard);
	}

	private initInteractiveFilters() {
		this.dashboard.filters.changeHandler?.onChange(async () => {
			this.updateRouteState();
		});
	}

	public applyKeywordFilter(keyword: string) {
		this.dashboardFiltersService.applyKeywordFilter(this.dashboard, keyword);
	}

	public cleanUpFilters() {
		this.dashboardFiltersService.cleanUpFilters(this.dashboard);
	}

	private updateSizesAndPriorities() {
		const getXY = (widgetInstance: WidgetInstance) => ({ x: widgetInstance.el?.gridstackNode?.x ?? 0, y: widgetInstance.el?.gridstackNode?.y ?? 0 });
		this.dashboard.widgetInstances
			.filter((widgetInstance) => !widgetInstance.hidden)
			.toSorted((widgetInstanceA, widgetInstanceB) => {
				const { x: xA, y: yA } = getXY(widgetInstanceA);
				const { x: xB, y: yB } = getXY(widgetInstanceB);
				const yDiff = yA - yB;
				return yDiff !== 0 ? yDiff : xA - xB;
			})
			.forEach((widgetInstance, index) => {
				widgetInstance.priority = index;
				if (widgetInstance.el?.gridstackNode) {
					widgetInstance.width = widgetInstance.el.gridstackNode.w ?? 1;
					widgetInstance.height = widgetInstance.el.gridstackNode.h ?? 1;
				}
			});
	}

	private async saveDashboard() {
		this.updateSizesAndPriorities();
		await this.dashboardService.saveDashboard(this.dashboard);
	}

	private handleGridChanges() {
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
	}

	private prepareWidgetInstanceScopeVm(widgetInstance: WidgetInstance): WidgetInstanceScope['vm'] {
		return {
			page: this,
			id: widgetInstance.id,
			widgetInstance,
			onWidgetEdit: () => {
				this.editWidget(widgetInstance);
				this.trackWidgetInstanceTelemetry(`Widget Configure clicked`, widgetInstance, {
					sender: 'layout',
				});
			},
			onWidgetRemove: () => {
				this.hideWidgetInstance(widgetInstance);
				this.trackWidgetInstanceTelemetry(`Widget made hidden`, widgetInstance, {
					sender: 'layout',
				});
			},
			onWidgetDrilldownClick: (type: string) => {
				this.handleWidgetDrilldown(widgetInstance, type);
				this.featureTrackingService.track(FeatureTrackingType.reportDrillDown, FeatureTrackingUseCases.reportingToolkit);
				this.trackWidgetInstanceTelemetry(`Drilldown clicked`, widgetInstance, {
					type,
				});
			},
		};
	}

	public async trackWidgetInstanceTelemetry(eventName: string, widgetInstance: WidgetInstance, extraProps?: Record<string, string>) {
		const widget = await this.dashboardService.getWidget(widgetInstance.widgetId || '');
		const entityId = this.dashboardService.getWidgetInstanceRecordType(widgetInstance);
		const entityType = entityId ? (await this.schemaService.getType(entityId, true))?.InternalName : undefined;

		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: eventName,
			widgetType: widget?.Name,
			mx42_uux_widget_instance_title: widgetInstance.titleEn || widgetInstance.title || '',
			mx42_uux_widget_instance_id: widgetInstance.id,
			entityId,
			entityType,
			...(extraProps || {}),
		});
	}

	private trackNewWidgetInstanceAdded(widgetInstance: WidgetInstance, source: string) {
		this.trackWidgetInstanceTelemetry(`New Widget Instance added`, widgetInstance, {
			source,
		});
	}

	private getWidgetInstanceTemplate() {
		return `<mx-dashboard-widget
					data-page="vm.page"
					id="{{'id-' + vm.widgetInstance.id}}"
					data-widget-instance="vm.widgetInstance"
					on-edit="vm.onWidgetEdit()"
					on-remove="vm.onWidgetRemove()"
					on-title-click="vm.onWidgetDrilldownClick('full')"
					on-select-item="vm.onWidgetDrilldownClick('series')"
					data-template="vm.widgetInstance.templateData.widgetTemplate"
					role="listitem"
				></mx-dashboard-widget>`;
	}

	private updateWidgetInstanceInGridster(widgetInstance: WidgetInstance) {
		if (widgetInstance.scope) {
			const vm = this.prepareWidgetInstanceScopeVm(widgetInstance);
			Object.assign(widgetInstance.scope?.vm || {}, vm);
		}

		if (widgetInstance.el) {
			this.gridStack.update(widgetInstance.el, {
				w: Math.max(widgetInstance.width, widgetInstance.minWidth),
				h: Math.max(widgetInstance.height, widgetInstance.minHeight),
				minH: widgetInstance.minHeight,
				minW: widgetInstance.minWidth,
			});
		}
	}

	private prepareWidgetEl(widgetInstance: WidgetInstance) {
		const localScope = this.$rootScope.$new(false, this.$scope) as WidgetInstanceScope;
		localScope.vm = this.prepareWidgetInstanceScopeVm(widgetInstance);
		const el = this.$compile(this.getWidgetInstanceTemplate())(localScope);
		widgetInstance.el = el[0];
		widgetInstance.scope = localScope;
		return widgetInstance.el;
	}

	private addWidgetsToGridster(widgetInstances: WidgetInstance[]) {
		this.gridStack.batchUpdate(true);
		widgetInstances.forEach((widgetInstance) => this.addWidgetToGridster(widgetInstance));
		this.gridStackEngine.justify();
		this.gridStack.batchUpdate(false);
	}

	private addWidgetToGridster(widgetInstance: WidgetInstance) {
		const el = this.prepareWidgetEl(widgetInstance);
		const height = Math.max(widgetInstance.height, widgetInstance.minHeight);

		this.gridStack.addWidget(el, {
			id: widgetInstance.id,
			autoPosition: true,
			w: Math.max(widgetInstance.width, widgetInstance.minWidth),
			h: height,
			minW: widgetInstance.minWidth,
			maxW: 12,
			minH: widgetInstance.minHeight,
			sizeToContent: this.isWidgetInstanceStretchable(widgetInstance) ? height : false,
		});
	}

	private isWidgetInstanceStretchable(widgetInstance: WidgetInstance) {
		return widgetInstance.dataModel.Context.ExportMode && widgetInstance.dataModel.Context.Stretchable;
	}

	private removeWidgetInstanceFromGridstack(widgetInstance: WidgetInstance) {
		this.gridStack.removeWidget(`#id-${widgetInstance.id}`);
		widgetInstance.el = undefined;
		widgetInstance.scope = undefined;
		this.gridStack.compact('compact');
	}

	private removeWidgetInstanceFromDashboard(widgetInstance: WidgetInstance) {
		const index = this.dashboard.widgetInstances.indexOf(widgetInstance);
		if (index >= 0) {
			this.dashboard.widgetInstances.splice(index, 1);
		}
	}

	public async hideWidgetInstance(widgetInstance: WidgetInstance) {
		if (this.configurableWidget === widgetInstance) {
			const cancelled = await this.cancelEditing();
			if (!cancelled) {
				return;
			}
		}

		this.removeWidgetInstanceFromGridstack(widgetInstance);
		widgetInstance.hidden = true;
		if (widgetInstance.isNew) {
			this.removeWidgetInstanceFromDashboard(widgetInstance);
		}

		await this.saveDashboard();
	}

	public async removeWidgetInstance(widgetInstance: WidgetInstance) {
		this.removeWidgetInstanceFromGridstack(widgetInstance);
		this.removeWidgetInstanceFromDashboard(widgetInstance);
		await this.dashboardService.removeWidgetInstance(widgetInstance.id);
		await this.saveDashboard();
	}

	public async moveWidgetInstance(widgetInstance: WidgetInstance, navigationId: string) {
		this.removeWidgetInstanceFromGridstack(widgetInstance);
		this.removeWidgetInstanceFromDashboard(widgetInstance);
		await this.dashboardService.moveWidgetInstance(widgetInstance.id, navigationId);
		await this.saveDashboard();
	}

	public async showWidgetInstance(widgetInstance: WidgetInstance) {
		widgetInstance.hidden = false;
		if (!widgetInstance.templateData) {
			await this.dashboardService.refreshWidgetInstance(widgetInstance, true, this.reportMode);
		}
		this.addWidgetToGridster(widgetInstance);
		this.gridStackEngine.justify();
		await this.saveDashboard();
	}

	public highlightWidgetInstance(widgetInstance: WidgetInstance) {
		if (widgetInstance.el?.scrollIntoViewIfNeeded) {
			widgetInstance.el?.scrollIntoViewIfNeeded();
		} else {
			widgetInstance.el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		}

		this.highlightedWidget = widgetInstance;
	}

	public hideHighlight() {
		this.highlightedWidget = undefined;
	}

	public async addEmptyWidgetInstance() {
		const priority = this.dashboard.widgetInstances.reduce((maxPriority, widgetInstance) => Math.max(maxPriority, widgetInstance.priority + 1), 0);
		const widgetInstance = await this.dashboardService.prepareNewWidgetInstance({
			dashboard: this.dashboard,
			widget: undefined,
			title: undefined,
			priority,
			hidden: false,
			width: this.gridStack.getColumn(),
			height: 1,
		});

		await this.addNewWidgetInstance(widgetInstance);
	}

	public async addNewWidgetInstance(widgetInstance: WidgetInstance) {
		if (!widgetInstance.templateData && widgetInstance.widgetId) {
			await this.dashboardService.refreshWidgetInstance(widgetInstance, true, this.reportMode);
		}
		this.dashboard.widgetInstances.push(widgetInstance);
		await this.dashboardFiltersService.reinitDashboardFilters(this.dashboard);
		this.addWidgetToGridster(widgetInstance);
		this.gridStackEngine.justify();
		await this.editWidget(widgetInstance);
	}

	public async applyCategory(category: WidgetCategory) {
		if (!this.configurableWidget) {
			return;
		}

		await this.dashboardService.changeWidgetInstanceCategory(this.configurableWidget, category.id);
		this.updateWidgetInstanceInGridster(this.configurableWidget);
		this.configurationMode = DashboardConfigurationMode.WidgetConfiguration;

		const widgetInstance = this.configurableWidget;
		this.$timeout(() => {
			if (widgetInstance === this.configurableWidget) {
				this.highlightWidgetInstance(widgetInstance);
			}
		}, 500);
	}

	public async applyWidget(widget: DashboardWidgetDTO) {
		if (!this.configurableWidget || this.configurableWidget.widgetId === widget.Id) {
			return;
		}
		await this.dashboardService.changeWidgetInWidgetInstance(this.configurableWidget, widget.Id);
		await this.dashboardService.refreshWidgetInstance(this.configurableWidget, false, this.reportMode);
		this.updateWidgetInstanceInGridster(this.configurableWidget);
		this.highlightWidgetInstance(this.configurableWidget);
	}

	public enableConfigurationMode() {
		if (this.gridStack.opts.columnOpts) {
			this.gridStack.opts.columnOpts.breakpoints = undefined;
		}

		this.gridStack.setStatic(false);
		this.configurationMode = DashboardConfigurationMode.ExistWidgets;
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
		this.eventService.dispatchEvent(EventCodes.DashboardPageConfigurationModeChanged, {
			configurationMode: this.configurationMode,
		});
	}

	public async disableConfigurationMode() {
		if (this.gridStack.opts.columnOpts) {
			this.gridStack.opts.columnOpts.breakpoints = this.gridBreakboints;
		}
		const cancelled = await this.cancelEditing();
		if (!cancelled) {
			return;
		}
		await this.dashboardFiltersService.reinitDashboardFilters(this.dashboard);
		this.gridStack.setStatic(true);
		this.configurationMode = DashboardConfigurationMode.Disabled;

		this.eventService.dispatchEvent(EventCodes.DashboardPageConfigurationModeChanged, {
			configurationMode: this.configurationMode,
		});
	}

	public async editWidget(widgetInstance: WidgetInstance) {
		if (this.configurationMode !== DashboardConfigurationMode.ChooseCategory && this.configurationMode !== DashboardConfigurationMode.WidgetConfiguration) {
			this.preEditConfigurationMode = this.configurationMode;
		}
		const cancelled = await this.cancelEditing();
		if (!cancelled) {
			return;
		}
		this.configurationMode = widgetInstance.categoryId ? DashboardConfigurationMode.WidgetConfiguration : DashboardConfigurationMode.ChooseCategory;
		this.eventService.dispatchEvent(EventCodes.DashboardPageConfigurationModeChanged, {
			configurationMode: this.configurationMode,
		});

		this.$timeout(() => {
			this.configurableWidget = widgetInstance;
			this.highlightWidgetInstance(this.configurableWidget);
		});
	}

	private handleWidgetDrilldown(widgetInstance: WidgetInstance, type?: string) {
		if (this.embeddedMode) {
			this.onWidgetInstanceClick({ widgetInstance: widgetInstance });
		} else {
			if (this.configurationMode === DashboardConfigurationMode.Disabled && this.dashboardService.hasDrillDown(widgetInstance, type)) {
				this.goToWidgetInstanceDatasetViewNavigation(widgetInstance);
			}
		}
	}

	private async goToWidgetInstanceDatasetViewNavigation(widgetInstance: WidgetInstance) {
		const columnSearch = await this.dashboardFiltersService.getColumnSearchForWidgetInstance(this.dashboard, widgetInstance);
		const entityType = this.dashboardService.getWidgetInstanceRecordType(widgetInstance);
		const currentRoute = this.routingService.getCurrent();

		const groupingInfo = widgetInstance.dataModel.Context.GroupingInfo || {};

		this.routingService.go(
			'customDataSet',
			angular.merge(
				{
					appString: currentRoute.parameters.appName,
					embeddedWidgetInstance: widgetInstance,
					keyword: this.dashboard.filters.keyword,
					columnSearch,
					grouping: this.dashboardService.getWidgetInstanceGroupBy(widgetInstance),
					filters: this.dashboardService.getWidgetInstanceFilters(widgetInstance),
					dataQueryId: this.dashboardService.getWidgetInstanceDataQueryId(widgetInstance),
					contentWidgetId: this.dashboardService.getWidgetInstanceDatasetViewId(widgetInstance),
					entityTypeIds: entityType ? [entityType] : [],
					structures: '',
					isDrillDown: true,
				},
				groupingInfo
			),
			'hack-for-location-false'
		);
	}

	public reloadPage() {
		this.routingService.reload();
	}

	private async revertWidgetInstance(widgetInstance: WidgetInstance) {
		const reverted = await this.dashboardService.revertWidgetInstance(widgetInstance);
		if (reverted) {
			this.updateWidgetInstanceInGridster(widgetInstance);
		}
	}

	public async cancelEditing(force = false): Promise<boolean> {
		if (this.configurableWidget) {
			if (!force && this.dashboardService.isWidgetInstanceNotSaved(this.configurableWidget, 'form')) {
				try {
					await this.notificationService.confirm(this.i18n.get('shell.common.confirmation'), this.i18n.get('shell.dashboards.confirmCancel'));
				} catch (e) {
					return false;
				}
			}

			if (this.configurableWidget.isNew) {
				this.removeWidgetInstanceFromDashboard(this.configurableWidget);
				this.removeWidgetInstanceFromGridstack(this.configurableWidget);
				this.gridStackEngine.justify();
			} else {
				await this.revertWidgetInstance(this.configurableWidget);
			}
		}

		this.configurationMode = this.preEditConfigurationMode;
		this.eventService.dispatchEvent(EventCodes.DashboardPageConfigurationModeChanged, {
			configurationMode: this.configurationMode,
		});
		this.hideHighlight();
		this.configurableWidget = undefined;
		return true;
	}

	public async finishEditing() {
		if (!this.configurableWidget) {
			return;
		}

		if (this.configurableWidget.isNew) {
			const widgetInstance = this.configurableWidget;
			this.trackNewWidgetInstanceAdded(widgetInstance, 'Dashboard');
		}

		await this.saveWidgetInstance(this.configurableWidget);

		if (!this.embeddedMode) {
			this.configurationMode = this.preEditConfigurationMode;
			this.eventService.dispatchEvent(EventCodes.DashboardPageConfigurationModeChanged, {
				configurationMode: this.configurationMode,
			});
			this.hideHighlight();
			this.configurableWidget = undefined;
		}
	}

	public async saveWidgetInstance(widgetInstance: WidgetInstance) {
		await this.dashboardService.saveWidgetInstance(widgetInstance, 'form');
	}

	public async saveWidgetInstanceAsNew(dashboardNavigationId: string) {
		if (!this.configurableWidget) {
			return;
		}

		const dashboard = this.dashboard.navigationId === dashboardNavigationId ? this.dashboard : await this.dashboardService.getDashboard(dashboardNavigationId, false);
		const newWidgetInstance = await this.dashboardService.cloneWidgetInstanceToDashboard(this.configurableWidget, dashboard);

		if (newWidgetInstance) {
			if (this.embeddedMode) {
				this.removeWidgetInstanceFromDashboard(this.configurableWidget);
				this.removeWidgetInstanceFromGridstack(this.configurableWidget);
				this.configurableWidget = undefined;
				await this.addNewWidgetInstance(newWidgetInstance);
				this.trackNewWidgetInstanceAdded(newWidgetInstance, 'Grouped Grid');
			} else {
				await this.cancelEditing(true);
				if (this.dashboard.navigationId === dashboardNavigationId) {
					await this.addNewWidgetInstance(newWidgetInstance);
				}
				this.trackNewWidgetInstanceAdded(newWidgetInstance, 'Copy');
			}
		}
	}

	public async copyWidgetInstance(widgetInstance: WidgetInstance) {
		const newWidgetInstance = await this.dashboardService.prepareWidgetInstanceClone(widgetInstance, this.dashboard);
		if (newWidgetInstance) {
			newWidgetInstance.title = this.i18n.getFormatted('shell.dashboards.copiedWidgetTitle', newWidgetInstance.title);
			await this.addNewWidgetInstance(newWidgetInstance);
		}
	}

	public backToCategories() {
		if (this.configurableWidget?.isNew) {
			this.configurationMode = DashboardConfigurationMode.ChooseCategory;
		}
	}

	private prepareEmptyPage() {
		const emptyPageBodyHtml = $(
			`<span>${this.i18n
				.get('shell.dashboards.emptyPageBody')
				.replace('{mx-dashboard-page--new-widget}', `ng-click="vm.addEmptyWidgetInstance()" href="javascript:void(0)" role="button" tabindex="0" `)}
			</span>`
		);
		const externalLinkEl = emptyPageBodyHtml.find('a[target="_blank"]');
		externalLinkEl.attr('rel', 'noopener noreferrer');
		externalLinkEl.attr('aria-label', `${externalLinkEl.text()} (${this.i18n.get('shell.common.open-in-new-window')})`);
		this.emptyPageBody = emptyPageBodyHtml.html();
	}

	escapeHandler = (event: KeyboardEvent) => {
		if (event.key === 'Escape' && this.displayMode === DisplayMode.BigScreen) {
			this.$scope.$evalAsync(() => this.setDisplayMode(DisplayMode.Normal, true));
		}
	};

	setDisplayMode(displayMode: DisplayMode, byHuman = false) {
		if (this.displayMode === displayMode) {
			return;
		}

		this.displayMode = displayMode;

		if (this.displayMode === DisplayMode.BigScreen) {
			document.body.classList.add('mx-dashboard-page--tv-mode');
			document.addEventListener('keydown', this.escapeHandler);
			if (byHuman) {
				this.notificationService.info(this.i18n.get('solutionBuilderAgent.layouts.landing-page.exitBigScreenMessage'));
			}
			this.enableAutoRefresher();
		} else {
			document.body.classList.remove('mx-dashboard-page--tv-mode');
			document.removeEventListener('keydown', this.escapeHandler);
			this.disabledAutoRefresher();
		}

		this.eventService.dispatchEvent(EventCodes.DisplayModeChanged, this.displayMode, byHuman);
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
	}

	get isTvMode() {
		return this.displayMode === DisplayMode.BigScreen;
	}

	async setAutoRefresherInterval(intervalMinutes: AutoRefreshInterval) {
		await this.dashboardService.saveAutoRefreshInterval(this.dashboard, intervalMinutes);
		this.disabledAutoRefresher();
		this.enableAutoRefresher();
	}

	enableAutoRefresher() {
		if (this.autoRefresherTimer || this.dashboard.autoRefreshInterval === 0) {
			return;
		}

		const refreshInterval = this.dashboard.autoRefreshInterval * 60000;
		let nextRefreshTime = Date.now() + refreshInterval;

		this.autoRefresherTimer = setInterval(async () => {
			const currentTime = Date.now();
			if (currentTime >= nextRefreshTime) {
				await this.dataModelCacheService.resetCache();
				this.$scope.$evalAsync(() => {
					this.dashboard.widgetInstances
						.filter((widgetInstance) => !widgetInstance.hidden)
						.forEach((widgetInstance) => {
							delete widgetInstance.templateData;
							this.dashboardService.refreshWidgetInstance(widgetInstance);
						});
				});

				nextRefreshTime = currentTime + refreshInterval;
			}
		}, 60000);
	}

	disabledAutoRefresher() {
		if (this.autoRefresherTimer) {
			clearInterval(this.autoRefresherTimer);
			this.autoRefresherTimer = undefined;
		}
	}

	onMouseMove() {
		this.mouseMoved = true;
		this.mouseMovedFinishTime = Date.now() + 3000;

		this.mouseMovedTimeout =
			this.mouseMovedTimeout ||
			setInterval(() => {
				if ((this.mouseMovedFinishTime ?? 0) < Date.now()) {
					this.$scope.$evalAsync(() => {
						this.mouseMoved = false;
					});
					clearInterval(this.mouseMovedTimeout);
					this.mouseMovedFinishTime = undefined;
					this.mouseMovedTimeout = undefined;
				}
			}, 1000);
	}

	toggleDashboardDescription() {
		this.isDescrExpanded = !this.isDescrExpanded;
		this.personalizationService.setDashboardDescrVisibility(this.dashboard.navigationId, this.isDescrExpanded);
	}
}

export const DashboardPageComponent = {
	name: 'mxDashboardPage',
	controller: DashboardPageController,
	controllerAs: 'vm',
	template,
	bindings: {
		componentId: '<',
		dashboardLoader: '<',
		embeddedMode: '<',
		reportMode: '<',
		onWidgetInstanceClick: '&',
		onInitialized: '&',
	},
};
